/* ATYP Dimensions band: <atyp-dimensions>.
   Geometry lives in CSS custom properties (cm and px per cm); this module fits the unit to
   the stage, tweens the numbers with GSAP (transform and opacity only), projects the three
   dimension labels through the same rotation and perspective as the scene so the text stays
   crisp, and follows the buy box through Dawn's variantChange event. Fails open: without
   GSAP or with reduced motion the static state is complete and the size control still
   switches instantly. */

const TAG = 'atyp-dimensions';
const ENTRY_THRESHOLD = 0.3;
const DEG = Math.PI / 180;

function whenGsap(cb) {
  if (window.gsap) { cb(window.gsap); return; }
  let n = 0;
  const iv = setInterval(() => {
    if (window.gsap) { clearInterval(iv); cb(window.gsap); }
    else if (++n > 100) { clearInterval(iv); }
  }, 50);
}

function decimalsOf(v) {
  const m = String(v).match(/\.(\d+)/);
  return m ? m[1].length : 0;
}

/* rotateY(yaw) then rotateX(pitch), the order the CSS applies */
function rotate(p, yaw, pitch) {
  const cy = Math.cos(yaw), sy = Math.sin(yaw), cx = Math.cos(pitch), sx = Math.sin(pitch);
  const x1 = p[0] * cy + p[2] * sy;
  const z1 = -p[0] * sy + p[2] * cy;
  return [x1, p[1] * cx - z1 * sx, p[1] * sx + z1 * cx];
}

class AtypDimensions extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-dim-data]');
    if (!dataEl) return;
    try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    this.stage = this.querySelector('[data-dim-stage]');
    this.scene = this.querySelector('.dim-scene');
    this.labels = {
      w: this.querySelector('.dim-lab--w'),
      h: this.querySelector('.dim-lab--h'),
      d: this.querySelector('.dim-lab--d')
    };
    this.marks = Array.from(this.querySelectorAll('.dim-mark-lab')).map((el) => ({ el, w: parseFloat(el.getAttribute('data-w')) || 0 }));
    this.active = Number(this.dataset.active) || 0;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.counters = Array.from(this.querySelectorAll('[data-dim-count]')).map((el) => {
      let vals = [];
      try { vals = JSON.parse(el.getAttribute('data-dim-vals') || '[]'); } catch (e) { vals = []; }
      return { el, vals, num: vals.map((v) => parseFloat(v)), dec: vals.map(decimalsOf) };
    });
    const g = this.data.geom || {};
    const pick = (arr, f) => (arr && arr.length ? (arr[this.active] !== undefined ? arr[this.active] : arr[0]) : f);
    this.cur = { w: pick(g.w, 1), h: pick(g.h, 1), d: pick(g.d, 0) };
    this.off = { yaw: 0, pitch: 0 };
    if (this.stage) {
      this.fit();
      this.ro = new ResizeObserver(() => this.fit());
      this.ro.observe(this.stage);
    }
    this.setAttribute('data-ready', '');
    this.bindControl();
    this.bindVariant();
    whenGsap((gsap) => {
      this.gsap = gsap;
      this.ctx = gsap.context(() => {}, this);
      if (this.stage && !this.reduce.matches) {
        this.setupEntry();
        this.setupPointer();
      }
    });
  }

  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
    if (this.io) this.io.disconnect();
    if (this.ctx) this.ctx.revert();
    if (this.unsub) this.unsub();
    if (this.offPointer) this.offPointer();
  }

  cssNum(name) {
    return parseFloat(getComputedStyle(this).getPropertyValue(name)) || 0;
  }

  /* px per cm so the full composition at the largest size (cuboid, dimension rig, labels,
     size rail, ghost envelopes) fills the stage and sits centred. The footprint is taken at
     the largest size because the ghosts and the rail always extend to it, so the scene
     transform never depends on the selected size and switching sizes cannot drift. */
  fit() {
    const r = this.stage.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const g = this.data.geom || {};
    const mw = Math.max.apply(null, g.w || [1]);
    const mh = Math.max.apply(null, g.h || [1]);
    const md = g.d && g.d.length ? Math.max.apply(null, g.d) : 0;
    const yaw = this.cssNum('--dim-yaw') * DEG;
    const pitch = this.cssNum('--dim-pitch') * DEG;
    const gap = this.cssNum('--dim-g') || 24;
    const padX = this.cssNum('--dim-fit-x'), padY = this.cssNum('--dim-fit-y');
    const persp = this.cssNum('--dim-persp') || 1600;
    const measure = (lab, fw, fh) => {
      const el = lab && lab.firstElementChild;
      const b = el && el.getBoundingClientRect();
      return b && b.width > 1 ? [b.width, b.height] : [fw, fh];
    };
    const lw = measure(this.labels.w, 44, 15), lh = measure(this.labels.h, 44, 15), ld = measure(this.labels.d, 40, 15);
    /* pivot (cm), exactly as the CSS defines it */
    const cx = mw / 2, cy = -mh / 2, cz = -md / 2;
    /* composition anchors in scene px (cm * u plus px offsets) with the 2D boxes hung on them:
       b = [left, right, top, bottom] relative to the projected anchor */
    const wmin = Math.min.apply(null, g.w || [mw]);
    /* the depth rig travels with the selected width, so the envelope is evaluated with it at
       the smallest and at the largest width and the two are averaged: the residual offset is
       then split evenly between S and L instead of landing on one of them */
    const points = (u, wd) => {
      const W = mw * u, H = mh * u, D = md * u, none = [0, 0, 0, 0];
      const Wd = wd * u;
      const list = [];
      [0, W].forEach((x) => [-H, 0].forEach((y) => [-D, 0].forEach((z) => list.push({ p: [x, y, z], b: none }))));
      list.push({ p: [0, gap + 5, 0], b: none }, { p: [W, gap + 5, 0], b: none });
      list.push({ p: [W / 2, gap + 10, 0], b: [-lw[0] / 2, lw[0] / 2, 0, lw[1]] });
      list.push({ p: [-gap - 4, -H, 0], b: none }, { p: [-gap - 4, 0, 0], b: none });
      list.push({ p: [-gap - 10, -H / 2, 0], b: [-lh[0], 0, -lh[1] / 2, lh[1] / 2] });
      if (this.labels.d) {
        list.push({ p: [Wd + gap, -H - 4, 0], b: none }, { p: [Wd + gap, -H - 4, -D], b: none });
        list.push({ p: [Wd + gap + 10, -H, -D / 2], b: [0, ld[0], -ld[1] / 2, ld[1] / 2] });
      }
      this.marks.forEach((m) => list.push({ p: [m.w * u, gap - 7, 0], b: [-6, 6, -10, 0] }));
      return list;
    };
    const union = (items, map) => {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      items.forEach((it) => {
        const q = map(it.p);
        minX = Math.min(minX, q[0] + it.b[0]); maxX = Math.max(maxX, q[0] + it.b[1]);
        minY = Math.min(minY, q[1] + it.b[2]); maxY = Math.max(maxY, q[1] + it.b[3]);
      });
      return { minX, maxX, minY, maxY, w: maxX - minX, h: maxY - minY };
    };
    /* the rotated extents grow monotonically with u but piecewise (label boxes are fixed px),
       so the largest unit that fits is found by bisection rather than a linear solve */
    const mean = (a, b) => ({ minX: (a.minX + b.minX) / 2, maxX: (a.maxX + b.maxX) / 2, minY: (a.minY + b.minY) / 2, maxY: (a.maxY + b.maxY) / 2 });
    const envelope = (u, map) => { const e = mean(union(points(u, wmin), map), union(points(u, mw), map)); e.w = e.maxX - e.minX; e.h = e.maxY - e.minY; return e; };
    const rotated = (u) => envelope(u, (p) => rotate([p[0] - cx * u, p[1] - cy * u, p[2] - cz * u], yaw, pitch));
    const availW = r.width - 2 * padX, availH = r.height - 2 * padY;
    let lo = 0.5, hi = 4000;
    for (let i = 0; i < 48; i++) {
      const mid = (lo + hi) / 2;
      const e = rotated(mid);
      if (e.w <= availW && e.h <= availH) lo = mid; else hi = mid;
    }
    const u = lo * 0.97;
    this.style.setProperty('--u', u.toFixed(3));
    this.view = { w: r.width, h: r.height, u, ox: 0, oy: 0, yaw, pitch, persp, gap, pivot: [cx * u, cy * u, cz * u] };
    /* centre the projected envelope, perspective included; one refinement pass settles it */
    const off = this.off;
    this.off = { yaw: 0, pitch: 0 }; /* centre the resting view, not a pointer parallax snapshot */
    for (let pass = 0; pass < 2; pass++) {
      const e = envelope(u, (p) => this.projectPoint(p));
      this.view.ox += r.width / 2 - (e.minX + e.maxX) / 2;
      this.view.oy += r.height / 2 - (e.minY + e.maxY) / 2;
    }
    this.off = off;
    this.scene.style.setProperty('--ox', this.view.ox.toFixed(1) + 'px');
    this.scene.style.setProperty('--oy', this.view.oy.toFixed(1) + 'px');
    this.updateLabels();
  }

  /* Same transform chain as .dim-scene plus the perspective of .dim-view (origin 50% 44%) */
  projectPoint(p) {
    const v = this.view;
    const q = rotate([p[0] - v.pivot[0], p[1] - v.pivot[1], p[2] - v.pivot[2]], v.yaw + this.off.yaw * DEG, v.pitch + this.off.pitch * DEG);
    const x = v.w / 2 + v.ox + q[0], y = v.h / 2 + v.oy + q[1], z = q[2];
    const ox = v.w * 0.5, oy = v.h * 0.44;
    const k = v.persp / (v.persp - z);
    return [ox + (x - ox) * k, oy + (y - oy) * k];
  }

  updateLabels() {
    if (!this.view) return;
    const u = this.view.u, g = this.view.gap;
    const W = this.cur.w * u, H = this.cur.h * u, D = this.cur.d * u;
    const place = (el, p, anchor) => {
      if (!el) return;
      const s = this.projectPoint(p);
      el.style.transform = 'translate3d(' + s[0].toFixed(1) + 'px,' + s[1].toFixed(1) + 'px,0) ' + anchor;
    };
    place(this.labels.w, [W / 2, g + 10, 0], 'translate(-50%, 0)');
    place(this.labels.h, [-g - 10, -H / 2, 0], 'translate(-100%, -50%)');
    place(this.labels.d, [W + g + 10, -H, -D / 2], 'translate(0, -50%)');
    this.marks.forEach((m) => place(m.el, [m.w * u, g - 7, 0], 'translate(-50%, -100%)'));
  }

  applyGeom() {
    this.style.setProperty('--dw', this.cur.w);
    this.style.setProperty('--dh', this.cur.h);
    this.style.setProperty('--dd', this.cur.d);
    this.updateLabels();
  }

  bindControl() {
    this.buttons = Array.from(this.querySelectorAll('[data-dim-size]'));
    this.buttons.forEach((b) => {
      b.addEventListener('click', () => this.setSize(Number(b.getAttribute('data-dim-size')), true));
    });
  }

  bindVariant() {
    /* Dawn declares PUB_SUB_EVENTS with const, a global lexical binding rather than a window property */
    let events = null, sub = null;
    try { events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS; } catch (e) { events = null; }
    try { sub = typeof subscribe === 'function' ? subscribe : window.subscribe; } catch (e) { sub = null; }
    if (!events || !events.variantChange || typeof sub !== 'function') return;
    this.unsub = sub(events.variantChange, (ev) => {
      const v = ev && ev.data && ev.data.variant;
      if (!v || !Array.isArray(v.options)) return;
      const d = this.data;
      if (d.sizeIdx >= 0 && Array.isArray(d.sizes)) {
        const i = d.sizes.findIndex((s) => s.raw === v.options[d.sizeIdx]);
        if (i >= 0 && i !== this.active) this.setSize(i, true);
      }
      if (d.colorIdx >= 0 && d.finishes) {
        const c = d.finishes[v.options[d.colorIdx]];
        if (c) this.style.setProperty('--dim-obj', c);
      }
    });
  }

  formatCount(c, i, value) {
    const dec = Math.max(c.dec[i] || 0, c.dec[this.active] || 0);
    return value.toFixed(dec);
  }

  setCountInstant(c, i) {
    if (!c.vals.length) return;
    const v = c.vals[i] !== undefined ? c.vals[i] : c.vals[0];
    c.el.textContent = v;
  }

  setSize(i, animate) {
    const g = this.data.geom || {};
    const prev = this.active;
    this.active = i;
    this.setAttribute('data-active', String(i));
    this.buttons.forEach((b, k) => {
      b.classList.toggle('is-active', k === i);
      b.setAttribute('aria-pressed', String(k === i));
    });
    const pick = (arr, f) => (arr && arr.length ? (arr[i] !== undefined ? arr[i] : arr[0]) : f);
    const target = { w: pick(g.w, 1), h: pick(g.h, 1), d: pick(g.d, 0) };
    const gsap = this.gsap;
    const motion = animate && gsap && !this.reduce.matches;
    if (!motion) {
      this.cur = target;
      this.applyGeom();
      this.style.setProperty('--dws', target.w);
      this.style.setProperty('--ctl-i', i);
      this.counters.forEach((c) => this.setCountInstant(c, i));
      return;
    }
    this.ctx.add(() => {
      const tl = gsap.timeline({ defaults: { overwrite: 'auto' } });
      tl.to(this.cur, { w: target.w, h: target.h, d: target.d, duration: 0.8, ease: 'power3.out', onUpdate: () => this.applyGeom() }, 0)
        .to(this, { '--dws': target.w, duration: 0.8, ease: 'power3.out' }, 0.07)
        .to(this, { '--ctl-i': i, duration: 0.55, ease: 'power3.out' }, 0);
      this.counters.forEach((c, k) => {
        if (!c.vals.length) return;
        const from = c.num[prev], to = c.num[i];
        if (isFinite(from) && isFinite(to)) {
          if (from === to) { this.setCountInstant(c, i); return; }
          const o = { v: from };
          tl.to(o, { v: to, duration: 0.75, ease: 'power3.out', onUpdate: () => { c.el.textContent = this.formatCount(c, i, o.v); }, onComplete: () => this.setCountInstant(c, i) }, 0.03 + k * 0.03);
        } else {
          tl.to(c.el, { opacity: 0, duration: 0.2, ease: 'power2.in', onComplete: () => this.setCountInstant(c, i) }, 0)
            .to(c.el, { opacity: 1, duration: 0.35, ease: 'power2.out' }, 0.25);
        }
      });
    });
  }

  /* Scroll entry: the object rises, the lines draw, the labels land */
  setupEntry() {
    const gsap = this.gsap;
    this.ctx.add(() => {
      const q = gsap.utils.selector(this);
      const tl = gsap.timeline({ paused: true });
      tl.from(this.scene, { '--rise': 28, duration: 1.05, ease: 'power3.out' }, 0)
        .from(q('.dim-face'), { opacity: 0, duration: 0.75, stagger: 0.07, ease: 'power2.out' }, 0)
        .from(q('.dim-edge'), { opacity: 0, duration: 0.6, ease: 'power2.out' }, 0.3)
        .from(q('.dim-shadow'), { opacity: 0, duration: 0.9, ease: 'power2.out' }, 0.12)
        .from(this.scene, { '--draw-w': 0, duration: 0.7, ease: 'power2.inOut' }, 0.5)
        .from(this.scene, { '--tkw': 0, duration: 0.45, ease: 'back.out(2)' }, 0.5)
        .from(this.scene, { '--draw-h': 0, duration: 0.7, ease: 'power2.inOut' }, 0.64)
        .from(this.scene, { '--tkh': 0, duration: 0.45, ease: 'back.out(2)' }, 0.64)
        .from(this.scene, { '--draw-d': 0, duration: 0.7, ease: 'power2.inOut' }, 0.78)
        .from(this.scene, { '--tkd': 0, duration: 0.45, ease: 'back.out(2)' }, 0.78)
        .from(q('.dim-ext, .dim-mark'), { opacity: 0, duration: 0.4, stagger: 0.05, ease: 'power2.out' }, 0.5)
        .from(q('.dim-ghost'), { opacity: 0, duration: 0.7, ease: 'power2.out', clearProps: 'opacity' }, 1.1)
        .from(q('.dim-mark-lab'), { opacity: 0, duration: 0.5, stagger: 0.08, ease: 'power2.out' }, 1.0)
        .from(q('.dim-lab-i'), { opacity: 0, y: 6, duration: 0.55, stagger: 0.14, ease: 'power2.out' }, 1.0)
        .from(q('.dim-ctl, .dim-one, .dim-row, .dim-note'), { opacity: 0, y: 14, duration: 0.65, stagger: 0.06, ease: 'power2.out' }, 0.55);
      this.entryTl = tl;
    });
    this.io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        this.io.disconnect();
        this.io = null;
        if (this.entryTl) this.entryTl.play();
      }
    }, { threshold: ENTRY_THRESHOLD });
    this.io.observe(this.stage);
  }

  /* A few degrees of pointer parallax on fine pointers only */
  setupPointer() {
    if (!window.matchMedia('(pointer: fine) and (hover: hover)').matches) return;
    const gsap = this.gsap;
    const apply = () => {
      this.style.setProperty('--dim-yaw-off', this.off.yaw.toFixed(2) + 'deg');
      this.style.setProperty('--dim-pitch-off', this.off.pitch.toFixed(2) + 'deg');
      this.updateLabels();
    };
    let yawTo, pitchTo;
    this.ctx.add(() => {
      yawTo = gsap.quickTo(this.off, 'yaw', { duration: 0.9, ease: 'power3.out', onUpdate: apply });
      pitchTo = gsap.quickTo(this.off, 'pitch', { duration: 0.9, ease: 'power3.out', onUpdate: apply });
    });
    let rect = null;
    const enter = () => { rect = this.stage.getBoundingClientRect(); };
    const move = (e) => {
      if (!rect) rect = this.stage.getBoundingClientRect();
      const nx = Math.min(1, Math.max(-1, ((e.clientX - rect.left) / rect.width - 0.5) * 2));
      const ny = Math.min(1, Math.max(-1, ((e.clientY - rect.top) / rect.height - 0.5) * 2));
      yawTo(nx * 3);
      pitchTo(-ny * 2);
    };
    const leave = () => { rect = null; yawTo(0); pitchTo(0); };
    this.stage.addEventListener('pointerenter', enter, { passive: true });
    this.stage.addEventListener('pointermove', move, { passive: true });
    this.stage.addEventListener('pointerleave', leave, { passive: true });
    this.offPointer = () => {
      this.stage.removeEventListener('pointerenter', enter);
      this.stage.removeEventListener('pointermove', move);
      this.stage.removeEventListener('pointerleave', leave);
    };
  }
}

if (!customElements.get(TAG)) customElements.define(TAG, AtypDimensions);
